// v1.9 P0: Textarea 原子组件
// 用 Input 同样的 label/error/hint/size/block 模式
import { type TextareaHTMLAttributes, forwardRef, useId } from 'react';
import { twMerge } from 'tailwind-merge';

export type TextareaSize = 'sm' | 'md' | 'lg';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  size?: TextareaSize;
  block?: boolean;
  required?: boolean;
  containerClassName?: string;
}

const SIZE_CLASS: Record<TextareaSize, string> = {
  sm: 'px-2.5 py-1.5 text-xs',
  md: 'px-3 py-2 text-sm',
  lg: 'px-3.5 py-2.5 text-base',
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    error,
    hint,
    size = 'md',
    block = false,
    required,
    containerClassName,
    id: idProp,
    className,
    rows = 3,
    disabled,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const errorId = error ? `${id}-err` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={twMerge(block && 'w-full', containerClassName)}>
      {label && (
        <label htmlFor={id} className="block text-xs text-ink-2 mb-1.5 select-none">
          {label}
          {required && (
            <span className="text-bad ml-0.5" aria-hidden>
              *
            </span>
          )}
        </label>
      )}
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        disabled={disabled}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        className={twMerge(
          'w-full bg-surface border rounded-md text-ink resize-y',
          'placeholder:text-ink-3',
          'transition-colors duration-150',
          'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15',
          'disabled:bg-surface-2 disabled:text-ink-3 disabled:cursor-not-allowed',
          error
            ? 'border-bad focus:border-bad focus:ring-bad/15'
            : 'border-line hover:border-ink-3',
          SIZE_CLASS[size],
          className,
        )}
        {...rest}
      />
      {error && (
        <p id={errorId} role="alert" className="text-xs text-bad mt-1">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={hintId} className="text-xs text-ink-3 mt-1">
          {hint}
        </p>
      )}
    </div>
  );
});
